// URL-driven language helpers. English lives at the canonical path (/faq); Chinese
// lives under a /zh prefix (/zh/faq) so each language has a distinct, crawlable URL
// with proper hreflang — the SEO/AEO-correct model. One source of truth for the
// prefix logic, used by the router, header/footer links, and the language switcher.
import { useLocation } from 'react-router-dom';

export type Lang = 'en' | 'zh';

export function langFromPath(pathname: string): Lang {
  return pathname === '/zh' || pathname.startsWith('/zh/') ? 'zh' : 'en';
}

// Strip the /zh prefix → the canonical English path (keeps any #hash/?query intact).
export function enPathOf(pathname: string): string {
  if (pathname === '/zh') return '/';
  if (pathname.startsWith('/zh/')) return pathname.slice(3);
  return pathname;
}

// Prefix an English path (which may carry a #hash) for the given language.
export function localizePath(path: string, lang: Lang): string {
  if (lang !== 'zh') return path;
  if (path === '/') return '/zh';
  return path.startsWith('/zh') ? path : `/zh${path}`;
}

// Portal & admin surfaces (/admin*, /intake*, /triage*) render OUTSIDE the
// LangLayout tree — see src/router/config.tsx: only '/' and '/zh' mount the
// shared page tree, so ONLY those routes have an /en + /zh URL pair. The portal
// surfaces have NO /zh variant and carry their OWN in-page language toggle, so
// the global URL language switcher must be scoped to the localized routes: point
// it at a portal path and localizePath() builds a /zh<portal> URL that 404s
// (the T75 bug). This list enumerates the non-localized top-level route prefixes;
// src/i18n/__tests__/localized-routes.test.ts locks it against the router, so a
// new portal route added without a matching prefix here fails the build.
export const NON_LOCALIZED_PREFIXES = ['/admin', '/intake', '/triage', '/consult-prep', '/reschedule'] as const;

// True when `pathname` is a URL-localized (public) route — one that has an /en
// and /zh variant and so may be driven by the global language switcher. False
// for the portal/admin surfaces. The /zh prefix is stripped first so both
// language variants of a public path classify identically; the prefix match is
// per-segment ('/admin' and '/admin/x' match, '/administrator' does not).
export function isLocalizedPath(pathname: string): boolean {
  const enPath = enPathOf(pathname);
  return !NON_LOCALIZED_PREFIXES.some(
    (prefix) => enPath === prefix || enPath.startsWith(`${prefix}/`),
  );
}

export function useLang(): Lang {
  return langFromPath(useLocation().pathname);
}
